<template>
  <div>
    <!-- Slot để hiển thị thông tin phía trên chart -->
    <slot name="header"></slot>

    <!-- Chart wrapper -->
    <div class="chart-wrapper">
      <bar-chart-component
        ref="barChart"
        :chart-data="processedChartData"
        :chart-options="mergedOptions"
      />
    </div>

    <!-- Slot để hiển thị thông tin phía dưới chart -->
    <slot name="footer"></slot>
  </div>
</template>

<script>
  import { Bar } from 'vue-chartjs'

  const BarChartComponent = {
    extends: Bar,
    props: ['chartData', 'chartOptions'],
    mounted() {
      this.renderChart(this.chartData, this.chartOptions)
    },
    methods: {
      reRender() {
        this.renderChart(this.chartData, this.chartOptions)
      },
    },
    watch: {
      chartData() {
        this.renderChart(this.chartData, this.chartOptions)
      },
    },
  }

  export default {
    name: 'BarChart',
    components: { BarChartComponent },
    props: {
      chartData: {
        type: Object,
        required: true,
        default: () => ({ labels: [], datasets: [] }),
      },
      chartOptions: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      threshold: {
        type: Number,
        required: false,
        default: 0,
      },
      showLegend: {
        type: Boolean,
        required: false,
        default: false,
      },
      customColors: {
        type: Array,
        required: false,
        default: () => [],
      },
      thresholdColors: {
        type: Object,
        required: false,
        default: () => ({
          success: '#4ee672',
          danger: '#d3404f',
        }),
      },
    },

    computed: {
      processedChartData() {
        if (!this.chartData || !this.chartData.datasets) {
          return this.chartData
        }

        // Tự động xử lý backgroundColor cho datasets
        const processedDatasets = this.chartData.datasets.map((dataset, index) => {
          let backgroundColor = dataset.backgroundColor

          // Nếu có customColors, sử dụng custom colors
          if (this.customColors.length > 0) {
            backgroundColor = this.customColors[index] || this.customColors[0]
          }
          // Nếu có threshold và data, tính màu dựa trên threshold (chỉ cho single dataset)
          else if (
            dataset.data &&
            Array.isArray(dataset.data) &&
            this.threshold > 0 &&
            this.chartData.datasets.length === 1
          ) {
            backgroundColor = dataset.data.map(value =>
              value >= this.threshold ? this.thresholdColors.success : this.thresholdColors.danger
            )
          }
          // Nếu không có backgroundColor và là multiple datasets, dùng default colors
          else if (!backgroundColor && this.chartData.datasets.length > 1) {
            const defaultColors = [
              '#007bff',
              '#28a745',
              '#dc3545',
              '#ffc107',
              '#17a2b8',
              '#6610f2',
              '#e83e8c',
              '#fd7e14',
              '#20c997',
              '#6f42c1',
            ]
            backgroundColor = defaultColors[index % defaultColors.length]
          }

          return {
            ...dataset,
            backgroundColor,
          }
        })

        return {
          ...this.chartData,
          datasets: processedDatasets,
        }
      },

      mergedOptions() {
        // Tự động tính max value từ chartData
        const maxValue = this.getMaxValueFromData()
        const adjustedMax = maxValue > 0 ? maxValue * 1.1 : undefined

        // Merge custom options với default options
        const defaultOptions = {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                gridLines: { display: false },
                ticks: {
                  fontSize: 12,
                  fontColor: '#6c757d',
                },
              },
            ],
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  fontSize: 12,
                  fontColor: '#6c757d',
                  callback: value => value.toLocaleString(),
                  ...(adjustedMax && { max: adjustedMax }),
                },
                gridLines: { color: '#e9ecef' },
              },
            ],
          },
          legend: {
            display: this.showLegend,
            position: 'top',
            labels: {
              fontSize: 12,
              fontColor: '#6c757d',
              padding: 20,
            },
          },
          tooltips: {
            backgroundColor: '#212529',
            titleFontSize: 13,
            bodyFontSize: 12,
            xPadding: 10,
            yPadding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              label(tooltipItem) {
                return tooltipItem.yLabel.toLocaleString()
              },
            },
          },
          animation: {
            duration: 1000,
            easing: 'easeOutQuart',
          },
        }

        // Deep merge với lodash-style merge logic
        return this.deepMerge(defaultOptions, this.chartOptions)
      },
    },

    methods: {
      reRender() {
        this.$nextTick(() => {
          if (this.$refs.barChart) {
            this.$refs.barChart.reRender()
          }
        })
      },

      getMaxValueFromData() {
        const data = this.processedChartData || this.chartData
        if (!data || !data.datasets || data.datasets.length === 0) {
          return 0
        }

        const allValues = data.datasets.reduce((acc, dataset) => {
          if (dataset.data && Array.isArray(dataset.data)) {
            return acc.concat(dataset.data.filter(val => typeof val === 'number'))
          }
          return acc
        }, [])

        return allValues.length > 0 ? Math.max(...allValues) : 0
      },

      deepMerge(target, source) {
        const result = { ...target }

        for (const key in source) {
          if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            result[key] = this.deepMerge(target[key] || {}, source[key])
          } else {
            result[key] = source[key]
          }
        }

        return result
      },
    },

    watch: {
      processedChartData: {
        handler() {
          this.reRender()
        },
        deep: true,
      },

      chartOptions: {
        handler() {
          this.reRender()
        },
        deep: true,
      },

      threshold() {
        this.reRender()
      },
    },
  }
</script>

<style scoped>
  .chart-wrapper {
    height: 400px;
    position: relative;
  }
</style>
